import { apiService } from './api';

export type AccessStatus = 'subscribed' | 'unlocked' | 'unlockable' | 'open';

/**
 * 解锁扣费（credit，与后端一致）：
 *  practice + Math_practice = 1；practice + SAT = 2；sat = 3；ap = 2；其它兜底 2。
 */
export function unlockCost(examType: string, subject?: string): number {
  if (examType === 'practice') return subject === 'SAT' ? 2 : 1;
  if (examType === 'sat') return 3;
  if (examType === 'ap') return 2;
  return 2;
}

export interface AttemptSummary {
  attemptId: string;
  objectiveScore: number | null;
  objectiveTotal: number | null;
  submittedAt: string;
  hasReport: boolean;
}

export interface ExamCard {
  id: string;
  title: string;
  subject: string;
  tier: string;
  year: number | null;
  month: string | null;
  region: string | null;
  accessStatus: AccessStatus;
  lastAttempt: AttemptSummary | null;
}

/** 交卷 payload：单题作答项 */
export interface UserAnswerItem {
  subject: string;
  moduleNo: number;
  sort: number;
  value: any;
}

export interface SubmitAttemptPayload {
  answers: UserAnswerItem[];
  frqResponses?: Record<string, any>;
  partDurations?: Record<string, number>;
}

export interface SubmitAttemptResult {
  attemptId: string;
  objectiveScore: number | null;
  objectiveTotal: number | null;
  reportPath: string;
}

export interface ReportQuestion {
  subject: string;
  moduleNo: number;
  sort: number;
  userAnswer: string;   // 展示值（未作答为 '-'）
  correct: string;      // 标准答案原始串
  isCorrect: boolean;
}

export interface ReportContent {
  attemptId: string;
  examId: string;
  submittedAt: string;
  markdown: string;
  answers: UserAnswerItem[];       // 用户逐题作答项
  questions: ReportQuestion[];     // 结构化核对结果（答案文件缺失时为空）
}

export interface ExamGroup {
  subject: string;
  items: ExamCard[];
}

export interface ExamListResponse {
  examType: string;
  subscribed: boolean;
  years: number[];
  groups: ExamGroup[];
}

// ---- 试卷内容（做题界面用）----
export interface QuestionOption {
  label: string;
  html: string;
}
export interface QuestionPart {
  label: string;
  index: number;
  html: string;
}
export interface Question {
  sort: number;
  displayNo: string | null;
  id: string;
  type: 'choice' | 'response';
  title?: string;
  passage?: { html: string };
  question?: { html: string };
  options?: QuestionOption[];
  parts?: QuestionPart[];
}
export interface ContentModule {
  moduleNo: number;
  questions: Question[];
}
export interface ContentSection {
  subject: string;
  modules: ContentModule[];
}
export interface TemplateModule {
  moduleNo: number;
  questionCount: number;
  timeLimitSec: number | null;
  adaptive: boolean;
  calculator: 'allowed' | 'not_allowed' | null;
  directions?: { html: string };
  formula?: { html: string };
}
export interface TemplateSection {
  sectionNo: number;
  subject: string;
  name: string;
  modules: TemplateModule[];
}
export interface ExamContent {
  schemaVersion: number;
  exam: {
    id: string;
    title: string;
    examType: string;
    subjectName?: string;
    [k: string]: any;
  };
  template: { sections: TemplateSection[] };
  content: { sections: ContentSection[] };
}

export const examApi = {
  async list(type: 'sat' | 'ap' | 'practice'): Promise<ExamListResponse> {
    const res: any = await apiService.get(`/exams?type=${type}`);
    return res.data as ExamListResponse;
  },

  async getContent(id: string): Promise<ExamContent> {
    const res: any = await apiService.get(`/exams/${id}/content`);
    return res.data as ExamContent;
  },

  /** 用 1 credit 解锁一套 tier=trial 试卷；成功返回新余额 */
  async unlock(id: string): Promise<{ creditBalance: number }> {
    const res: any = await apiService.post(`/exams/${id}/unlock`);
    return res.data as { creditBalance: number };
  },

  /** 交卷：核对答案、生成报告 */
  async submitAttempt(id: string, payload: SubmitAttemptPayload): Promise<SubmitAttemptResult> {
    const res: any = await apiService.post(`/exams/${id}/attempts`, payload);
    return res.data as SubmitAttemptResult;
  },

  /** 取报告 markdown 内容 */
  async getReport(attemptId: string): Promise<ReportContent> {
    const res: any = await apiService.get(`/exams/attempts/${attemptId}/report`);
    return res.data as ReportContent;
  },
};
